import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  BugReport as TestIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  List as ListIcon,
  CloudQueue as CloudIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { log } from '../utils/errors/logger';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../components/ui/PageLayout';
import {
  AdminTabs,
  type AdminTab,
} from '../components/admin';
import { ErrorAlert, SuccessAlert } from '../components/ui/AlertMessage';
import {
  useGetTextExtractionProviders,
  useGetCurrentTextExtractionConfig,
  useGetAllTextExtractionConfigs,
  useSaveTextExtractionConfig,
  useTestTextExtractionConfig,
  useDeleteTextExtractionConfig,
  useResetTextExtractionConfig,
} from '@/hooks/api/config/useTextExtractionConfig';

interface TextExtractionProvider {
  value: string;
  label: string;
  description: string;
  supported_types: string[];
  required_config: string[];
  optional_config: string[];
  features?: string[];
  libraries?: string[];
}

interface TextExtractionConfig {
  provider: string;
  timeout: number;
  max_retries: number;
  ocr_enabled: boolean;
  ocr_language: string;
  extract_tables: boolean;
  extract_images: boolean;
  preserve_layout: boolean;
  provider_config: Record<string, any>;
  display_name: string;
  description: string;
  is_active?: boolean;
}

interface TestResult {
  success: boolean;
  health: boolean;
  provider_info: Record<string, any>;
  supported_types: string[];
  error: string;
  message: string;
}

const TextExtractionConfig: React.FC = () => {
  const { t } = useTranslation();
  const { data: providers = [], isLoading: isLoadingProviders } = useGetTextExtractionProviders();
  const { data: currentConfig, isLoading: isLoadingCurrent } = useGetCurrentTextExtractionConfig();
  const { data: allConfigs = [], isLoading: isLoadingAll } = useGetAllTextExtractionConfigs();
  const saveConfig = useSaveTextExtractionConfig();
  const testConfig = useTestTextExtractionConfig();
  const deleteConfig = useDeleteTextExtractionConfig();
  const resetConfig = useResetTextExtractionConfig();

  const [activeTab, setActiveTab] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [config, setConfig] = useState<TextExtractionConfig>({
    provider: '',
    timeout: 60,
    max_retries: 3,
    ocr_enabled: true,
    ocr_language: 'en',
    extract_tables: true,
    extract_images: false,
    preserve_layout: true,
    provider_config: {},
    display_name: '',
    description: '',
  });
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<string | null>(null);

  const isLoading = isLoadingProviders || isLoadingCurrent || isLoadingAll;

  // Set default provider when providers are loaded
  useEffect(() => {
    log.debug('Providers loaded', { providersCount: (providers as TextExtractionProvider[]).length, selectedProvider });
    if ((providers as TextExtractionProvider[]).length > 0 && !selectedProvider) {
      // Set Local provider as default
      const localProvider = (providers as TextExtractionProvider[]).find(p => p.value === 'local');
      log.debug('Local provider found', { localProvider });
      if (localProvider) {
        log.info('Setting local provider as default');
        handleProviderChange('local');
      } else if ((providers as TextExtractionProvider[]).length > 0) {
        // Fallback to first provider if local not found
        log.info('Setting first provider as default', { provider: (providers as TextExtractionProvider[])[0] });
        handleProviderChange((providers as TextExtractionProvider[])[0].value);
      }
    }
  }, [providers, selectedProvider]);

  // Set initial config from current config
  useEffect(() => {
    if (currentConfig && !selectedProvider) {
      const currentCfg = currentConfig as TextExtractionConfig;
      setSelectedProvider(currentCfg.provider);
      setConfig(currentCfg);
    }
  }, [currentConfig, selectedProvider]);

  const handleProviderChange = (providerValue: string) => {
    setSelectedProvider(providerValue);
    const provider = (providers as TextExtractionProvider[]).find(p => p.value === providerValue);
    
    if (provider) {
      setConfig({
        ...config,
        provider: providerValue,
        display_name: `${provider.label} Configuration`,
        description: provider.description,
        provider_config: {},
      });
    }
  };

  const handleConfigChange = (field: keyof TextExtractionConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProviderConfigChange = (key: string, value: any) => {
    setConfig(prev => ({
      ...prev,
      provider_config: {
        ...prev.provider_config,
        [key]: value,
      },
    }));
  };

  const testConfiguration = async () => {
    setTestResult(null);
    try {
      const result = await testConfig.mutateAsync(config);
      setTestResult(result as TestResult);
    } catch (error) {
      log.error('Error testing configuration', { error });
    }
  };

  const saveConfiguration = async () => {
    try {
      await saveConfig.mutateAsync(config as any);
    } catch (error) {
      log.error('Error saving configuration', { error });
    }
  };

  const deleteConfiguration = async (configId: string) => {
    try {
      await deleteConfig.mutateAsync(configId);
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
    } catch (error) {
      log.error('Error deleting configuration', { error });
    }
  };

  const resetToDefault = async () => {
    try {
      await resetConfig.mutateAsync();
    } catch (error) {
      log.error('Error resetting configuration', { error });
    }
  };

  const renderProviderConfigFields = () => {
    const provider = (providers as TextExtractionProvider[]).find(p => p.value === selectedProvider);
    if (!provider) {return null;}

    const allConfigFields = [...provider.required_config, ...provider.optional_config];

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('textExtractionConfig.form.providerConfiguration')}
        </Typography>
        <Grid container spacing={2}>
          {allConfigFields.map(field => (
            <Grid size={{ xs: 12, sm: 6 }} key={field}>
              <TextField
                fullWidth
                label={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                value={config.provider_config[field] || ''}
                onChange={(e) => handleProviderConfigChange(field, e.target.value)}
                required={provider.required_config.includes(field)}
                type={field.includes('key') || field.includes('secret') || field.includes('password') ? 'password' : 'text'}
                helperText={provider.required_config.includes(field) ? t('textExtractionConfig.form.required') : t('textExtractionConfig.form.optional')}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderCurrentConfig = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {t('textExtractionConfig.currentConfig.title')}
        </Typography>
        
        {isLoadingCurrent ? (
          <CircularProgress />
        ) : currentConfig ? (
          <Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">{t('textExtractionConfig.currentConfig.provider')}</Typography>
                <Typography variant="body1">{(currentConfig as TextExtractionConfig).display_name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">{t('textExtractionConfig.currentConfig.type')}</Typography>
                <Typography variant="body1">{(currentConfig as TextExtractionConfig).provider}</Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="body2" color="textSecondary">{t('textExtractionConfig.currentConfig.description')}</Typography>
                <Typography variant="body1">{(currentConfig as TextExtractionConfig).description}</Typography>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6">{t('textExtractionConfig.currentConfig.features')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Chip label={`${t('textExtractionConfig.features.ocr')}: ${(currentConfig as TextExtractionConfig).ocr_enabled ? t('textExtractionConfig.features.enabled') : t('textExtractionConfig.features.disabled')}`} />
                <Chip label={`${t('textExtractionConfig.features.tables')}: ${(currentConfig as TextExtractionConfig).extract_tables ? t('textExtractionConfig.features.yes') : t('textExtractionConfig.features.no')}`} />
                <Chip label={`${t('textExtractionConfig.features.images')}: ${(currentConfig as TextExtractionConfig).extract_images ? t('textExtractionConfig.features.yes') : t('textExtractionConfig.features.no')}`} />
                <Chip label={`${t('textExtractionConfig.features.layout')}: ${(currentConfig as TextExtractionConfig).preserve_layout ? t('textExtractionConfig.features.preserved') : t('textExtractionConfig.features.notPreserved')}`} />
                <Chip label={`${t('textExtractionConfig.features.timeout')}: ${(currentConfig as TextExtractionConfig).timeout}s`} />
                <Chip label={`${t('textExtractionConfig.features.maxRetries')}: ${(currentConfig as TextExtractionConfig).max_retries}`} />
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography>{t('textExtractionConfig.currentConfig.noActiveConfig')}</Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderAllConfigs = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">{t('textExtractionConfig.messages.allConfigurations')}</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={resetToDefault}
            disabled={resetConfig.isPending}
          >
            {t('textExtractionConfig.actions.reset')}
          </Button>
        </Box>

        {isLoadingAll ? (
          <CircularProgress />
        ) : (
          <List>
            {Array.isArray(allConfigs) && allConfigs.length > 0 ? (allConfigs as TextExtractionConfig[]).map((cfg, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={cfg.display_name}
                  secondary={
                    <Box>
                      <Typography variant="body2">{cfg.description}</Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip size="small" label={cfg.provider} />
                        {cfg.is_active && <Chip size="small" label={t('textExtractionConfig.messages.active')} color="primary" sx={{ ml: 1 }} />}
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {!cfg.display_name.includes('Environment Default') && (
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setConfigToDelete(index.toString());
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            )) : (
              <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                {t('textExtractionConfig.messages.noConfigurationsFound')}
              </Typography>
            )}
          </List>
        )}
      </CardContent>
    </Card>
  );

  const renderAvailableProviders = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {t('textExtractionConfig.providers.title')}
        </Typography>

        {Array.isArray(providers) && (providers as TextExtractionProvider[]).map(provider => (
          <Accordion key={provider.value}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{provider.label}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Typography variant="body1" paragraph>
                  {provider.description}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  {t('textExtractionConfig.providers.supportedTypes')}:
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {provider.supported_types.map(type => (
                    <Chip key={type} label={type.toUpperCase()} size="small" sx={{ mr: 1, mb: 1 }} />
                  ))}
                </Box>

                {provider.features && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('textExtractionConfig.providers.features')}:
                    </Typography>
                    <Box>
                      {provider.features.map(feature => (
                        <Chip key={feature} label={feature} variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
                      ))}
                    </Box>
                  </Box>
                )}

                {provider.libraries && (
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {t('textExtractionConfig.providers.libraries')}:
                    </Typography>
                    <Typography variant="body2">
                      {provider.libraries.join(', ')}
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    handleProviderChange(provider.value);
                    setActiveTab(3); // Switch to configuration tab
                  }}
                >
                  {t('textExtractionConfig.actions.configure')}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>
    </Card>
  );

  const renderConfiguration = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {t('textExtractionConfig.messages.configureProvider')}
        </Typography>

        <Grid container spacing={3}>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t('textExtractionConfig.form.provider')}</InputLabel>
              <Select
                value={selectedProvider}
                onChange={(e) => handleProviderChange(e.target.value as string)}
                disabled={isLoadingProviders || (providers as TextExtractionProvider[]).length === 0}
              >
                {Array.isArray(providers) && (providers as TextExtractionProvider[]).length > 0 ? (providers as TextExtractionProvider[]).map(provider => (
                  <MenuItem key={provider.value} value={provider.value}>
                    {provider.label}
                  </MenuItem>
                )) : (
                  <MenuItem value="" disabled>
                    {isLoadingProviders ? t('textExtractionConfig.messages.loadingProviders') : t('textExtractionConfig.messages.noProvidersAvailable')}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('textExtractionConfig.form.displayName')}
              value={config.display_name}
              onChange={(e) => handleConfigChange('display_name', e.target.value)}
              required
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('textExtractionConfig.form.description')}
              value={config.description}
              onChange={(e) => handleConfigChange('description', e.target.value)}
              multiline
              rows={2}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('textExtractionConfig.form.timeout')}
              type="number"
              value={config.timeout}
              onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('textExtractionConfig.form.maxRetries')}
              type="number"
              value={config.max_retries}
              onChange={(e) => handleConfigChange('max_retries', parseInt(e.target.value))}
            />
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.ocr_enabled}
                  onChange={(e) => handleConfigChange('ocr_enabled', e.target.checked)}
                />
              }
              label={t('textExtractionConfig.form.ocrEnabled')}
            />
          </Grid>

          {config.ocr_enabled && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('textExtractionConfig.form.ocrLanguage')}
                value={config.ocr_language}
                onChange={(e) => handleConfigChange('ocr_language', e.target.value)}
              />
            </Grid>
          )}

          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.extract_tables}
                  onChange={(e) => handleConfigChange('extract_tables', e.target.checked)}
                />
              }
              label={t('textExtractionConfig.form.extractTables')}
            />
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.extract_images}
                  onChange={(e) => handleConfigChange('extract_images', e.target.checked)}
                />
              }
              label={t('textExtractionConfig.form.extractImages')}
            />
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.preserve_layout}
                  onChange={(e) => handleConfigChange('preserve_layout', e.target.checked)}
                />
              }
              label={t('textExtractionConfig.form.preserveLayout')}
            />
          </Grid>
        </Grid>

        {renderProviderConfigFields()}

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<TestIcon />}
            onClick={testConfiguration}
            disabled={testConfig.isPending || !selectedProvider}
          >
            {testConfig.isPending ? t('textExtractionConfig.messages.testing') : t('textExtractionConfig.actions.test')}
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveConfiguration}
            disabled={saveConfig.isPending || !selectedProvider || !config.display_name}
          >
            {t('textExtractionConfig.actions.saveAndActivate')}
          </Button>
        </Box>
        
        {testResult && (
          <Paper sx={{ mt: 2, p: 2 }}>
            <Alert severity={testResult.success ? 'success' : 'error'}>
              <Typography variant="body1">{testResult.message}</Typography>
              {testResult.error && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('textExtractionConfig.testResult.errorDetails')}: {testResult.error}
                </Typography>
              )}
            </Alert>

            {testResult.success && testResult.provider_info && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary">{t('textExtractionConfig.testResult.providerInfo')}:</Typography>
                <Typography variant="body2">
                  {t('textExtractionConfig.testResult.health')}: {testResult.health ? `✅ ${t('textExtractionConfig.testResult.healthy')}` : `❌ ${t('textExtractionConfig.testResult.unhealthy')}`}
                </Typography>
                <Typography variant="body2">
                  {t('textExtractionConfig.testResult.supportedTypes')}: {testResult.supported_types.join(', ')}
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </CardContent>
    </Card>
  );

  // Define tabs with standardized structure
  const tabs: AdminTab[] = [
    {
      label: t('textExtractionConfig.tabs.currentConfiguration'),
      icon: <SettingsIcon />,
      content: renderCurrentConfig(),
    },
    {
      label: t('textExtractionConfig.tabs.allConfigurations'),
      icon: <ListIcon />,
      content: renderAllConfigs(),
    },
    {
      label: t('textExtractionConfig.tabs.availableProviders'),
      icon: <CloudIcon />,
      content: renderAvailableProviders(),
    },
    {
      label: t('textExtractionConfig.tabs.createConfiguration'),
      icon: <AddIcon />,
      content: renderConfiguration(),
    },
  ];

  return (
    <>
      <PageLayout
        title={t('textExtractionConfig.title')}
        isLoading={isLoading}
        maxWidth="xl"
      >
        {saveConfig.isSuccess && <SuccessAlert message={t('textExtractionConfig.messages.saveSuccess')} onClose={() => saveConfig.reset()} />}
        {deleteConfig.isSuccess && <SuccessAlert message={t('textExtractionConfig.messages.deleteSuccess')} onClose={() => deleteConfig.reset()} />}
        {resetConfig.isSuccess && <SuccessAlert message={t('textExtractionConfig.messages.resetSuccess')} onClose={() => resetConfig.reset()} />}

        {saveConfig.isError && <ErrorAlert message={t('textExtractionConfig.messages.saveFailed')} onClose={() => saveConfig.reset()} />}
        {testConfig.isError && <ErrorAlert message={t('textExtractionConfig.messages.testFailed')} onClose={() => testConfig.reset()} />}
        {deleteConfig.isError && <ErrorAlert message={t('textExtractionConfig.messages.deleteFailed')} onClose={() => deleteConfig.reset()} />}
        {resetConfig.isError && <ErrorAlert message={t('textExtractionConfig.messages.resetFailed')} onClose={() => resetConfig.reset()} />}

        <AdminTabs
          tabs={tabs}
          value={activeTab}
          onChange={setActiveTab}
          variant="standard"
        />
      </PageLayout>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('textExtractionConfig.dialog.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('textExtractionConfig.dialog.deleteMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('textExtractionConfig.dialog.cancel')}</Button>
          <Button
            onClick={() => configToDelete && deleteConfiguration(configToDelete)}
            color="error"
            disabled={deleteConfig.isPending}
          >
            {t('textExtractionConfig.dialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TextExtractionConfig;